import { BaseAdapter } from './base';
import {
  type EnvironmentInfo,
  type PlatformCapabilities,
  PlatformEnum,
} from '../utils';
import type { AsyncStorage } from '../core';
import { EnvironmentDetector } from '../core';
import { CookieUrlMap, LiteAppGlobalDataConfig } from '../types';

/** 读取构建期注入的 cookieUrl（未配置 chainWebpack DefinePlugin 时返回 undefined） */
function getBuildTimeCookieUrl(): CookieUrlMap | undefined {
  // 用 typeof 守卫避免未替换时抛 ReferenceError
  return typeof __WPHK_COOKIE_URL__ !== 'undefined' ? __WPHK_COOKIE_URL__ : undefined;
}

/**
 * Liteapp 平台适配器
 * 将 Liteapp 的 API 映射到统一的 Platform 接口
 */
export class LiteappAdapter extends BaseAdapter {
  readonly platformType: PlatformEnum = PlatformEnum.LiteApp;
  readonly env: EnvironmentInfo;
  readonly capabilities: PlatformCapabilities;

  constructor() {
    super();
    this.env = EnvironmentDetector.getEnvironmentInfo();
    this.capabilities = EnvironmentDetector.detectCapabilities(this.platformType);
  }

  // ========== 重写getter以直接访问liteapp ==========
  // get scrollX(): number {
  //   return 0;
  // }

  // get scrollY(): number {
  //   return 0;
  // }

  // override get document(): Document {
  //   // TODO: 实现liteapp的document
  // }

  // override addEventListener(
  //   type: string,
  //   listener: EventListenerOrEventListenerObject,
  //   options?: boolean | AddEventListenerOptions,
  // ): void {
  //   if (type === 'resize' && typeof lite !== 'undefined' && lite.onWindowResize) {
  //     lite.onWindowResize(listener);
  //   } else {
  //     // 使用基类的默认实现
  //     this.defaultAddEventListener(type, listener, options);
  //   }
  // }

  // override removeEventListener(
  //   type: string,
  //   listener: EventListenerOrEventListenerObject,
  //   options?: boolean | EventListenerOptions,
  // ): void {
  //   if (type === 'resize' && typeof lite !== 'undefined' && lite.offWindowResize) {

  //   } else {
  //     // 使用基类的默认实现
  //     this.defaultRemoveEventListener(type, listener, options);
  //   }
  // }
  //
  // override requestAnimationFrame(callback: FrameRequestCallback): number {
  //   if (typeof lite !== 'undefined' && lite.requestAnimationFrame) {
  //     return lite.requestAnimationFrame(callback);
  //   }
  //   // 使用 setTimeout 模拟
  //   return setTimeout(callback, 16) as unknown as number;
  // }

  // override fetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  //   if (typeof lite !== 'undefined' && lite.request) {
  //     // 将 fetch 转换为 lite.request
  //     return this.liteappRequest(input, init);
  //   }
  //   // 使用基类的默认实现
  //   return this.defaultFetch(input, init);
  // }

  protected getDefaultLocalStorage(): AsyncStorage {
    return {
      get length(): number {
        return 0; // LiteApp KV 不支持获取长度
      },
      key: function(index: number): string | null {
        return null; // LiteApp KV 不支持按索引获取键名
      },
      clear: function(): void {
        lite.kv.clear();
      },
      /** 特别小心，这里是异步读取！！ */
      getItem: async function(key: string): Promise<string | null> {
        const value = await lite.kv.getItem(key);
        if (value === undefined || value === null) return null;
        return value;
      },
      removeItem: function(key: string): void {
        (lite.kv.removeItem(key) as any)?.catch?.(() => {
          // 静默处理
        });
      },
      setItem: function(key: string, value: string): void {
        if (value === undefined || value === null) return;
        (lite.kv.setItem(key, value) as any)?.catch?.(() => {
          // 静默处理
        });
      }
    };
  }
  /**
   * 获取Cookie
   * @param {string} name - 要获取的Cookie名称
   * @returns {string} Cookie值或空字符串（如果不存在）
   */
  protected getDefaultCookie(name: string): string {
    const { cookieUrl } = this.getGlobalDataConfig();
    if (!cookieUrl) {
      console.warn('liteapp getCookie need cookieUrl via liteapp.config chainWebpack DefinePlugin');
      return '';
    }
    const cookies = lite.cookie.get(cookieUrl) || '';
    const regex = new RegExp(`(^| )${name}=([^;]+)`);
    const match = cookies.match(regex);
    return match?.[2] ? decodeURIComponent(match[2]) : '';
  }

  /**
   * 设置Cookie
   * @param {string} name - Cookie名称
   * @param {string} value - Cookie值
   * @param {number} days - 过期天数（默认7天）
   * @param {string} path - 有效路径（默认'/'）
   * @param {string} domain - 有效域名（默认当前域名）
   * @param {boolean} secure - 是否仅HTTPS传输（默认false）
   */
  protected setDefaultCookie(options: { name: string; value: string; days?: number | undefined; path?: string | undefined; domain?: string | undefined; secure?: boolean | undefined; }): void {
    const { name, value, days = 7, path = '', domain = '', secure = false } = options;
    if (!name || !value || !domain || !path) {
      console.error('liteapp setCookie name, value, domain and path are required');
      return;
    }
    let cookieString = `${name}=${encodeURIComponent(value)}`;
    // 设置过期时间
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      cookieString += `; expires=${date.toUTCString()}`;
    }
    // 设置路径
    cookieString += `; path=${path}`;
    // 设置域名
    if (domain) {
      cookieString += `; domain=${domain}`;
    }
    // 设置Secure标志
    if (secure) {
      cookieString += '; Secure';
    }
    // !! 注意：如果 setCookie 之后在 liteapp 开发者工具看不到，需要https://${domain}${path} 先发起请求，liteapp 开发者工具才能看到，但是实际上是 setCookie 成功的。
    const url = `https://${domain}${path}`;
    lite.cookie.set(url, cookieString);
  }

  // protected doDefaultFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  //   if (typeof lite !== 'undefined' && lite.request) {
  //     return lite.request(input, init);
  //   }
  //   return Promise.reject(new Error('fetch not supported'));
  // }

  protected getDefaultNavigator(): Navigator {
    return {
      userAgent: lite.system.getUserAgent(),
      sendBeacon: lite.system.sendBeacon,
    } as Navigator;
  }

  protected getDefaultLocation(): Location {
    return {
      pathname: lite.router?.currentRoute || '',
    } as Location;
  }

  protected getDefaultDocument(): Document {
    return document;
  }

  /** 获取全局数据配置（读取构建期注入的 cookieUrl） */
  public getGlobalDataConfig(): Readonly<LiteAppGlobalDataConfig> {
    return { cookieUrl: getBuildTimeCookieUrl() };
  }
}
